"use client";
import { useEffect, useState, type MouseEvent } from "react";
import {
  Check,
  Minus,
  Plus,
  ShoppingBag,
  ChevronRight,
  ChevronLeft,
  Star,
  Truck,
  ShieldCheck,
  Clock3,
  Heart,
  Shapes,
  Palette,
  Sparkles,
} from "lucide-react";
import type { Product } from "@/lib/types";
import { money, number } from "@/lib/config";
import { maxQuantity } from "@/lib/cart";
import { useCart } from "./cart-provider";
import { ProductImage } from "./product-image";
import { ProductInfoTabs, type ProductInfoTab } from "./product-info-tabs";
import { useWishlist } from "./wishlist-store";
const colorSamples: Record<string, string> = {
  "مشکی": "#202227", "قهوه‌ای": "#82533a", "سفید": "#f8f7f1",
  "آبی": "#3277c9", "زرد": "#f1c949", "سبز": "#258459", "قرمز": "#c8393c",
  "صورتی": "#d783b9", "بنفش": "#8660a9", "خاکستری": "#8b9399", "طلایی": "#c49a48", "نقره‌ای": "#bbc2c6", "نارنجی": "#ed8137", "چندرنگ": "conic-gradient(#e86f52, #e9c75b, #65a677, #6390cb, #e86f52)",
  "سیاه و سفید": "linear-gradient(135deg, #222 50%, #fff 50%)",
  "رنگ شانسی": "conic-gradient(#a57556, #222, #eee, #a57556)",
};
function familyHeading(options: { product: Product }[], fallback: string): string {
  if (options.length < 2) return fallback;
  const names = options.map((option) => option.product.name.trim().split(/\s+/));
  const first = names[0];
  const shared: string[] = [];
  for (const [index, word] of first.entries()) {
    if (!names.every((name) => name[index] === word)) break;
    shared.push(word);
  }
  return shared.length >= 3 ? shared.join(" ") : fallback;
}
export function ProductDetail({ product: baseProduct, categoryName, initialSizeSlug = "", sizeOptions = [], optionKind = "size", canonicalSlug, freeShippingThreshold }: {
  product: Product;
  categoryName?: string;
  initialSizeSlug?: string;
  sizeOptions?: { product: Product; label: string; size: string }[];
  optionKind?: "size" | "color" | "model";
  canonicalSlug?: string;
  freeShippingThreshold: number;
}) {
  const [sizeSlug, setSizeSlug] = useState(initialSizeSlug || baseProduct.slug);
  const product = sizeOptions.find((option) => option.size === sizeSlug)?.product ?? baseProduct;
  const heading = optionKind === "color" ? familyHeading(sizeOptions, baseProduct.name) : product.name;
  const [selected, setSelected] = useState(product.variants[0]?.id),
    [quantity, setQuantity] = useState(1),
    [image, setImage] = useState(0),
    [added, setAdded] = useState(false);
  const [infoTab, setInfoTab] = useState<ProductInfoTab>("description");
  const [zoomActive, setZoomActive] = useState(false);
  useEffect(() => {
    if (!canonicalSlug || canonicalSlug === baseProduct.slug || sizeOptions.length < 2) return;
    window.history.replaceState(null, "", `/products/${canonicalSlug}?size=${encodeURIComponent(sizeSlug)}`);
  }, [baseProduct.slug, canonicalSlug, sizeOptions.length, sizeSlug]);
  useEffect(() => {
    const syncFromUrl = () => {
      const requested = new URLSearchParams(window.location.search).get("size") || baseProduct.slug;
      const next = sizeOptions.find((option) => option.size === requested)?.product ?? baseProduct;
      setSizeSlug(next.slug);
      setSelected(next.variants[0]?.id);
      setImage(0);
      setQuantity(1);
      setAdded(false);
    };
    window.addEventListener("popstate", syncFromUrl);
    return () => window.removeEventListener("popstate", syncFromUrl);
  }, [baseProduct, sizeOptions]);
  function chooseSize(next: Product) {
    setSizeSlug(next.slug);
    setSelected(next.variants[0]?.id);
    setImage(0);
    setQuantity(1);
    setAdded(false);
    const url = `/products/${canonicalSlug || baseProduct.slug}?size=${encodeURIComponent(next.slug)}`;
    window.history.pushState(null, "", url);
  }
  const { add, ready } = useCart();
  const wishlist = useWishlist();
  const v = product.variants.find((v) => v.id === selected);
  const max = v ? maxQuantity(v) : 0;
  const shownPrice = v?.price ?? product.basePrice;
  const approvedReviewCount = product.reviewsCount ?? 0;
  const averageRating = typeof product.rating === "number" && Number.isFinite(product.rating)
    ? product.rating
    : 0;
  const hasPrice = Boolean(shownPrice && Number.isFinite(shownPrice) && shownPrice > 0);
  const unitSavings = hasPrice && !product.demo && Number.isFinite(Number(product.oldPrice)) && Number(product.oldPrice) > Number(shownPrice)
    ? Number(product.oldPrice) - Number(shownPrice)
    : 0;
  const purchaseSavings = unitSavings * quantity;
  const canBuy = !product.demo && product.purchasable !== false && Boolean(v) && hasPrice && max > 0;
  const quantityLimit = max;
  const productFaqs = [
    {
      question: "این محصول از چه جنسی ساخته شده است؟",
      answer: baseProduct.material
        ? `جنس ثبت‌شده برای این محصول ${baseProduct.material} است. جزئیات رنگ و ساخت را در مشخصات همین صفحه ببینید.`
        : "این محصول با فیلامنت و به کمک چاپگر سه‌بعدی ساخته شده است. رنگ و جزئیات ثبت‌شده را در مشخصات محصول ببینید.",
    },
    {
      question: "آماده‌سازی محصول چقدر زمان می‌برد؟",
      answer: "آماده‌سازی محصولات چاپ سه‌بعدی معمولاً ۲ تا ۴ روز کاری زمان می‌برد.",
    },
    {
      question: "هزینهٔ ارسال چه زمانی رایگان می‌شود؟",
      answer: `هزینهٔ پست برای سفارش‌های بالای ${money(freeShippingThreshold)} رایگان است. هزینهٔ بسته‌بندی، در صورت اعمال، جداگانه محاسبه می‌شود.`,
    },
    {
      question: "در چه شرایطی امکان مرجوعی وجود دارد؟",
      answer: "اگر محصول هنگام تحویل آسیب‌دیده باشد یا با سفارش شما مغایرت داشته باشد، لطفاً از صفحهٔ پشتیبانی و با ذکر شمارهٔ سفارش، عکس یا ویدیویی از کالا بفرستید تا موضوع پیگیری شود.",
    },
  ];
  return (
    <div className="product-detail-page">
      <div className="detail-grid">
        <div className="detail-gallery">
          <div
            className={`detail-image${zoomActive ? " is-zooming" : ""}`}
            onMouseLeave={() => setZoomActive(false)}
            onMouseMove={(event: MouseEvent<HTMLDivElement>) => {
              if (!window.matchMedia("(hover: hover) and (pointer: fine)").matches) return;
              if ((event.target as HTMLElement).closest(".gallery-controls")) {
                setZoomActive(false);
                return;
              }
              setZoomActive(true);
              const bounds = event.currentTarget.getBoundingClientRect();
              const lensDiameter = 220;
              const scale = 2;
              const x = event.clientX - bounds.left;
              const y = event.clientY - bounds.top;
              const element = event.currentTarget;
              element.style.setProperty("--zoom-lens-left", `${x - lensDiameter / 2}px`);
              element.style.setProperty("--zoom-lens-top", `${y - lensDiameter / 2}px`);
              element.style.setProperty("--zoom-image-left", `${lensDiameter / 2 - x * scale}px`);
              element.style.setProperty("--zoom-image-top", `${lensDiameter / 2 - y * scale}px`);
              element.style.setProperty("--zoom-image-width", `${bounds.width * scale}px`);
              element.style.setProperty("--zoom-image-height", `${bounds.height * scale}px`);
            }}
            title="برای دیدن جزئیات، نشانگر را روی تصویر حرکت دهید"
          >
            <ProductImage
              key={product.images[image] || product.id}
              src={product.images[image]}
              alt={`${product.name}، نمای ${number(image + 1)}`}
              priority
              sizes="(max-width: 800px) 100vw, 50vw"
            />
            {zoomActive && (
              <span
                className="detail-image-zoom-lens"
                aria-hidden="true"
                style={{ left: "var(--zoom-lens-left, -999px)", top: "var(--zoom-lens-top, -999px)" }}
              >
                <span
                  className="detail-image-zoom-frame"
                  style={{ left: "var(--zoom-image-left, 0px)", top: "var(--zoom-image-top, 0px)", width: "var(--zoom-image-width, 0px)", height: "var(--zoom-image-height, 0px)" }}
                >
                  <ProductImage src={product.images[image]} alt="" sizes="100vw" />
                </span>
              </span>
            )}
            {product.images.length > 1 && (
              <div className="gallery-controls">
                <button
                  aria-label="تصویر قبلی"
                  onClick={() =>
                    setImage(
                      (image + product.images.length - 1) %
                        product.images.length,
                    )
                  }
                >
                  <ChevronRight />
                </button>
                <span aria-live="polite">
                  {number(image + 1)} / {number(product.images.length)}
                </span>
                <button
                  aria-label="تصویر بعدی"
                  onClick={() => setImage((image + 1) % product.images.length)}
                >
                  <ChevronLeft />
                </button>
              </div>
            )}
          </div>
          <div className="thumbnails detail-thumbnails">
              {product.images.map((src, i) => (
                <button
                  key={src}
                  aria-label={`نمای ${number(i + 1)}`}
                  aria-pressed={i === image}
                  onClick={() => setImage(i)}
                >
                  <ProductImage src={src} alt="" sizes="80px" />
                </button>
              ))}
          </div>
        </div>
        <div className="detail-copy">
          <span className="eyebrow">نکسو سه‌بعدی / چاپ سه‌بعدی</span>
          <h1>{heading}</h1>
          <button
            type="button"
            className="detail-save-button"
            disabled={!wishlist.ready}
            aria-pressed={wishlist.ids.includes(product.id)}
            onClick={() => wishlist.toggle(product.id)}
          >
            <Heart size={18} fill={wishlist.ids.includes(product.id) ? "currentColor" : "none"} aria-hidden="true" />
            {wishlist.ids.includes(product.id) ? "در علاقه‌مندی‌ها ذخیره شد" : "ذخیره در علاقه‌مندی‌ها"}
          </button>
          <div
            className="detail-rating-preview"
            aria-label={approvedReviewCount > 0
              ? `${number(averageRating)} از ۵ امتیاز، ${number(approvedReviewCount)} دیدگاه تأییدشده`
              : "هنوز امتیازی برای این محصول ثبت نشده است"}
          >
            <span className="detail-rating-stars" aria-hidden="true">
              {Array.from({ length: 5 }, (_, i) => (
                <Star key={i} size={17} fill={i < Math.round(averageRating) ? "currentColor" : "none"} />
              ))}
            </span>
            <span>{approvedReviewCount > 0
              ? `${number(Number(averageRating.toFixed(1)))} از ۵ · ${number(approvedReviewCount)} دیدگاه`
              : "هنوز امتیازی ثبت نشده"}</span>
            <a
              href="#product-information"
              onClick={() => setInfoTab("reviews")}
            >
              دیدگاه‌ها
            </a>
          </div>
          {(baseProduct.subtitle || baseProduct.description) && <p className="detail-intro">{baseProduct.subtitle || baseProduct.description}</p>}
          <div className="detail-key-features" role="group" aria-label="ویژگی‌های محصول">
            <div><Shapes size={19} aria-hidden="true" /><span><strong>دسته‌بندی و کاربرد</strong>{categoryName || "محصول چاپ سه‌بعدی"}؛ مناسب کلکسیون، استفادهٔ روزمره و هدیه</span></div>
            <div><Palette size={19} aria-hidden="true" /><span><strong>طرح و رنگ‌بندی</strong>ساخته‌شده با چاپ سه‌بعدی؛ رنگ و جزئیات هر مدل را در تصویر همان محصول ببینید.</span></div>
            <div><Sparkles size={19} aria-hidden="true" /><span><strong>شرایط نگهداری</strong>با دستمال نرم و خشک تمیز کنید؛ از شست‌وشو، حرارت و آفتاب مستقیم دور نگه دارید.</span></div>
          </div>
          {sizeOptions.length > 1 && <div className={`detail-size-picker${optionKind === "color" ? " is-color" : ""}`} role="group" aria-label={optionKind === "color" ? "انتخاب رنگ محصول" : "انتخاب مدل یا اندازهٔ محصول"}>
            <div className="detail-size-heading"><strong>{optionKind === "color" ? "انتخاب رنگ" : optionKind === "model" ? "انتخاب مدل" : "انتخاب اندازه"}</strong><span>{sizeOptions.find((option) => option.product.id === product.id)?.label}</span></div>
            <div className="detail-size-options">
              {sizeOptions.map((option) => {
                const prices = optionKind === "color" ? [] : option.product.variants.map((variant) => variant.price).filter((price) => Number.isFinite(price) && price > 0);
                const price = prices.length ? Math.min(...prices) : option.product.basePrice;
                return <button
                  type="button"
                  key={option.product.id}
                  onClick={() => chooseSize(option.product)}
                  className="detail-size-option"
                  aria-pressed={option.product.id === product.id}
                  aria-label={optionKind === "color" ? option.label : `${option.label}، ${option.product.name}، ${price && price > 0 ? money(price) : "قیمت ثبت نشده"}`}
                >
                  {optionKind === "color" ? <><i className="detail-color-swatch" style={{ background: colorSamples[option.label] || "var(--soft)" }} aria-hidden="true" /><strong>{option.label}</strong></> : <>
                    <span className="detail-size-thumb"><ProductImage src={option.product.images[0]} alt="" sizes="56px" /></span>
                    <span className="detail-size-text"><strong>{option.label}</strong><small>{option.product.name}</small><em>{price && price > 0 ? money(price) : "قیمت ثبت نشده"}</em></span>
                  </>}
                </button>;
              })}
            </div>
          </div>}
          <div className="detail-purchase-panel">
            <div className="detail-purchase-head">
              <span>قیمت محصول</span>
              <div className="detail-price-stack">
                {unitSavings > 0 && <del className="detail-original-price">{money(Number(product.oldPrice))}</del>}
                <strong className="detail-price">
                  {hasPrice && !product.demo && shownPrice
                    ? money(shownPrice)
                    : "قیمت ثبت نشده"}
                </strong>
              </div>
            </div>
            {purchaseSavings > 0 && <div className="detail-savings" aria-live="polite"><span>سود شما از این خرید</span><strong>{money(purchaseSavings)}</strong></div>}
            <div className={`availability${canBuy ? " is-orderable" : ""}`}>
              {product.demo
                ? "سفارش این محصول فعال نیست"
                : product.purchasable === false
                  ? "سفارش این محصول هنوز فعال نیست"
                : !hasPrice
                  ? "قیمت این رنگ ثبت نشده است"
                  : max > 0
                    ? v?.made_to_order ? "● قابل سفارش؛ آماده‌سازی پس از ثبت سفارش" : "● موجود و آماده ثبت سفارش"
                    : "در حال حاضر ناموجود"}
            </div>
            {!product.demo && product.variants.length > 1 && (
              <fieldset className="variant-field">
                <legend>رنگ انتخابی: {v?.color}</legend>
                <div className="color-options">
                  {product.variants.map((opt) => (
                    <button
                      key={opt.id}
                      onClick={() => {
                        setSelected(opt.id);
                        setQuantity(1);
                        setAdded(false);
                      }}
                      aria-pressed={selected === opt.id}
                      aria-label={opt.color}
                    >
                      {opt.hex && <span style={{ background: opt.hex }} />}
                      {opt.color}
                      {selected === opt.id && <Check size={15} />}
                    </button>
                  ))}
                </div>
              </fieldset>
            )}
            {product.demo && (
              <p className="detail-material-note">رنگ محصول در تصویر نمایش داده شده است.</p>
            )}
            <div className="add-row">
              <div className="quantity">
                <button
                  aria-label="کاهش تعداد"
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  disabled={!canBuy || quantity <= 1}
                >
                  <Minus size={16} />
                </button>
                <span aria-live="polite">{number(quantity)}</span>
                <button
                  aria-label="افزایش تعداد"
                  onClick={() => setQuantity(Math.min(quantityLimit, quantity + 1))}
                  disabled={!canBuy || quantity >= quantityLimit}
                >
                  <Plus size={16} />
                </button>
              </div>
              <button
                className="button primary"
                disabled={!canBuy || !ready}
                onClick={() => {
                  if (!v) return;
                  add(v.id, quantity, false, product);
                  setAdded(true);
                }}
              >
                {added ? <Check size={19} /> : <ShoppingBag size={19} />}{" "}
                {product.demo
                  ? "سفارش این محصول فعال نیست"
                  : product.purchasable === false
                    ? "سفارش این محصول هنوز فعال نیست"
                  : !hasPrice
                    ? "قیمت ثبت نشده"
                    : max < 1
                      ? "ناموجود"
                      : added
                        ? "به سبد اضافه شد"
                        : "افزودن به سبد خرید"}
              </button>
            </div>
          </div>
          <div className="detail-mobile-buy-bar" role="group" aria-label="خرید محصول">
            <span className="detail-mobile-price">{hasPrice && shownPrice ? money(shownPrice) : "قیمت ثبت نشده"}{unitSavings > 0 && <del>{money(Number(product.oldPrice))}</del>}</span>
            <div className="detail-mobile-quantity">
              <span>تعداد</span>
              <div className="quantity" role="group" aria-label="تعداد محصول">
                <button type="button" aria-label="کاهش تعداد" onClick={() => setQuantity(Math.max(1, quantity - 1))} disabled={!canBuy || quantity <= 1}><Minus size={16} aria-hidden="true" /></button>
                <span aria-live="polite">{number(quantity)}</span>
                <button type="button" aria-label="افزایش تعداد" onClick={() => setQuantity(Math.min(quantityLimit, quantity + 1))} disabled={!canBuy || quantity >= quantityLimit}><Plus size={16} aria-hidden="true" /></button>
              </div>
            </div>
            <button type="button" className="button primary" disabled={!canBuy || !ready} onClick={() => { if (!v) return; add(v.id, quantity, false, product); setAdded(true); }}>
              <ShoppingBag size={18} aria-hidden="true" /> {canBuy ? "افزودن به سبد خرید" : "فعلاً قابل سفارش نیست"}
            </button>
          </div>
        </div>
      </div>
      <div className="detail-service-grid">
        <div>
          <Truck size={22} />
          <strong>ارسال سریع</strong>
          <span>به سراسر کشور</span>
        </div>
        <div>
          <Clock3 size={22} />
          <strong>آماده‌سازی ۲ تا ۴ روز کاری</strong>
          <span>تولید سفارشی</span>
        </div>
        <div>
          <ShieldCheck size={22} />
          <strong>جزئیات روشن</strong>
          <span>عکس‌ها و مشخصات ثبت‌شده را یک‌جا ببینید.</span>
        </div>
      </div>
      <p className="detail-shipping-note">
        پست برای سفارش‌های بالای {money(freeShippingThreshold)} رایگان است.
      </p>
      <ProductInfoTabs
        product={baseProduct}
        reviewSlug={product.slug}
        active={infoTab}
        onChange={setInfoTab}
      />
      <section className="product-faq-section" aria-labelledby="product-faq-title">
        <div className="product-faq-heading">
          <span className="eyebrow">راهنمای انتخاب و سفارش</span>
          <h2 id="product-faq-title">پرسش‌های پرتکرار این محصول</h2>
        </div>
        <div className="product-faq-grid">
          {productFaqs.map((faq) => (
            <details className="product-faq-card" key={faq.question}>
              <summary>
                <span>{faq.question}</span>
                <span className="faq-plus" aria-hidden="true">+</span>
              </summary>
              <p>{faq.answer}</p>
            </details>
          ))}
        </div>
      </section>
    </div>
  );
}
